import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsRecognitions,
  type GoodsRecognitionItem,
} from '@/api/goodsWriteoffs';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  reason: string;
  comment: string;
  items: GoodsRecognitionItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  reason: '',
  comment: '',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsRecognitionEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.inventory.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.inventory.manage');
  const docQuery = useQuery({
    queryKey: ['goods-recognition', id],
    queryFn: () => goodsRecognitions.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'goods-recognition', (cid) => goodsRecognitions.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // 2026-08-12 (рішення власника): попап номенклатури з мультивибором, як у переміщенні.
  const [pickerOpen, setPickerOpen] = useState(false);
  const numberPreview = useNumberPreview('doc.goods_recognitions', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        reason: d.reason ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          unit_cost: String(it.unit_cost),
          amount: it.amount ? String(it.amount) : undefined,
          // 0415: склад рядка (fallback стара шапка для legacy-доків).
          warehouse_id: it.warehouse_id ?? d.warehouse_id ?? null,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const total = form.items.reduce(
    (s, e) => s + Number(e.quantity || 0) * Number(e.unit_cost || 0), 0,
  );

  const create = useMutation({
    mutationFn: () => goodsRecognitions.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      reason: form.reason || null,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
      navigate(`/goods-recognitions/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsRecognitions.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      reason: form.reason || null,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-recognition', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsRecognitions.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
          reason: form.reason || null,
        comment: form.comment || null,
        items: form.items,
      });
      try {
        await goodsRecognitions.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/goods-recognitions/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
      navigate(`/goods-recognitions/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await goodsRecognitions.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
          reason: form.reason || null,
        comment: form.comment || null,
        items: form.items,
      });
      await goodsRecognitions.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-recognition', id] });
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsRecognitions.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-recognition', id] });
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsRecognitions.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
      navigate('/goods-recognitions');
    },
  });

  function setItem(i: number, patch: Partial<GoodsRecognitionItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.inventory.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати оприбуткування товарів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-recognitions')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.886 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Товари', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове оприбуткування' : `Оприбуткування: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.goods_recognitions', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/goods-recognitions')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/goods-recognitions/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-recognitions/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_recognitions', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div className="field">
        <label>Причина (необов'язково)</label>
        <input value={form.reason}
               onChange={(e) => setForm({ ...form, reason: e.target.value })}
               disabled={isPosted}
               placeholder="напр., Знайдено на складі поза обліком" />
      </div>

      </DocBlock>

      <DocBlock index={2} title="Товари"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            title="Підбір з довідника; галочками можна обрати кілька товарів одразу">
            + додати номенклатуру
          </button>
        ) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 190 }}>Склад</th>
            <th style={{ width: 110 }}>Кількість</th>
            <th style={{ width: 130 }}>Ціна (собівартість)</th>
            <th style={{ width: 130 }}>Сума</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const amount = Number(it.quantity || 0) * Number(it.unit_cost || 0);
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td>
                  <NomenclatureSelect
                    value={it.nomenclature_id}
                    disabled={isPosted}
                    kind="goods"
                    onChange={(v) => setItem(i, { nomenclature_id: v })}
                  />
                </td>
                <td>
                  <select value={it.warehouse_id ?? ''}
                    onChange={(e) => setItem(i, { warehouse_id: e.target.value || null })}
                    disabled={isPosted}>
                    <option value="">—</option>
                    {warehousesQ.data?.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input type="number" step="0.0001" value={it.quantity}
                         onChange={(e) => setItem(i, { quantity: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td>
                  <input type="number" step="0.01" value={it.unit_cost}
                         onChange={(e) => setItem(i, { unit_cost: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fmt(amount)}
                </td>
                <td>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          multiSelect
          onPickMany={(rows) => {
            setForm((f) => ({
              ...f,
              items: [...f.items, ...rows.map((r, k) => ({
                line_number: f.items.length + k + 1,
                nomenclature_id: r.nomenclature_id,
                quantity: '1',
                unit_cost: '0',
                // Джерело — склад рядка залишків (якщо був обраний).
                warehouse_id: r.warehouse_id || null,
              }))],
            }));
            setPickerOpen(false);
          }}
          onPick={() => undefined}
          onClose={() => setPickerOpen(false)}
        />
      )}

      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.goods_recognitions" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> при проведенні створюється нова партія товару
          зі вказаною собівартістю, рухи `+` у регістрах товарів на складі і партій,
          бухпроводка <strong>Дт 281 — Кт 719</strong> на суму = кількість × ціна.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
